import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AuthCard, AuthMessage, FONT, AUTH_BACKGROUND, portalTitle } from '../SignInScreens';
import { mediaFileUrl, previewFileUrl } from '../mediaFileUrl';
import PortalSetPasswordScreen, { PORTAL_SET_PASSWORD_PATH, PORTAL_RESET_PASSWORD_PATH } from '../PortalSetPasswordScreen';
import PortalConfirmEmailScreen, { PORTAL_CONFIRM_EMAIL_PATH } from './PortalConfirmEmailScreen';
import PortalAccountPage from './PortalAccountPage';
import PortalOrdersPage from './PortalOrdersPage';
import PortalQuotesPage from './PortalQuotesPage';
import PortalShipmentsPage from './PortalShipmentsPage';
import PortalTermsPage from './PortalTermsPage';
import PortalHome from './PortalHome';
import { PortalOrderPage, PortalCartPage } from './PortalOrderingPage';
import { portalApi, getPortalSession, setPortalSession, getPortalSwitch, setPortalSwitch, getSwitchReturn, setSwitchReturn, sessionHeaders, portalErrorMessage, saveBlob } from './portalApi';

// The Customer portal (issue #254, vis_user_management.md Concept 8): every
// page under /portal/<portal name>, the tenant's own portal address. Served
// by AuthGate.js in place of the back office, so nothing staff-side renders
// or runs here. A logged-out visitor to any portal page sees the sign-in
// screen (with Forgot password, #256); a signed-in Customer sees the
// Home dashboard (PortalHome.js), their pricelist downloads at /portal/<name>/pricelists
// (#255), their account page at /portal/<name>/account (#256), and their
// Sales Orders at /portal/<name>/orders and /portal/<name>/orders/<id>
// (#257), ordering at /portal/<name>/order and /portal/<name>/cart
// (#284), and their Quotes at /portal/<name>/quotes and
// /portal/<name>/quotes/<id>.
//
// A tab opened by the Login Switcher (issue #283) arrives with
// #switch=<one-time link>, swaps it for a switch token (portalApi.js) and
// shows the portal as that login, under a fixed banner with Exit. Once the
// switch ends (Exit, time up, or the Admin starting another or signing out
// of the back office) the tab shows "Login switch ended" and nothing of the
// customer's. Exit, and that card's Back to the back office, take the tab to
// the back-office page the switch was started from (&back= on the link),
// already signed in since the staff session is shared across tabs.
//
// The backend runs on Render, which can take tens of seconds to cold-start
// — a network error or 502 there gets retried; only a real answer counts.

const MAX_ATTEMPTS = 6;
const RETRY_DELAY_MS = 5000;
const INPUT_STYLE = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, boxSizing: 'border-box' };
const PRIMARY_BUTTON = { padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, fontSize: 13 };
const SECONDARY_BUTTON = { padding: '6px 12px', border: '1px solid #bfc8d6', color: '#1f2d3d', background: '#fff', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };
const LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const LINK_BUTTON = { background: 'none', border: 'none', padding: 0, color: '#1976d2', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };
const ACCOUNT_PAGE = 'account';
const ORDERS_PAGE = 'orders';
const QUOTES_PAGE = 'quotes';
const SHIPMENTS_PAGE = 'shipments';
const PRICELISTS_PAGE = 'pricelists';
const ORDER_PAGE = 'order';
const CART_PAGE = 'cart';
const TERMS_PAGE = 'terms';
const SESSION_ENDED_NOTICE = 'Your session has ended. Sign in again.';
const SWITCH_STATUS_POLL_MS = 30 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function portalNameFromPath() {
  const rest = window.location.pathname.replace(/^\/portal\/?/, '');
  try {
    return decodeURIComponent(rest.split('/')[0] || '').toLowerCase();
  } catch {
    return '';
  }
}

// The page under the portal address: '' (welcome), 'account', 'orders',
// 'orders/<id>', 'quotes', 'quotes/<id>', 'terms' and so on.
function portalPageFromPath() {
  const [, page = '', id = ''] = window.location.pathname.replace(/^\/portal\/?/, '').split('/');
  if ([ACCOUNT_PAGE, PRICELISTS_PAGE, SHIPMENTS_PAGE, ORDER_PAGE, CART_PAGE, TERMS_PAGE].includes(page)) return page;
  if (page === ORDERS_PAGE || page === QUOTES_PAGE) return /^\d+$/.test(id) ? `${page}/${id}` : page;
  return '';
}

// The Login Switcher's one-time link from the address, removed from it so
// it isn't left in history or a copied address. The back-office page it was
// started from (&back=) is kept for when the switch ends.
function takeSwitchHandoff(portalName) {
  const match = window.location.hash.match(/^#switch=([^&]+)(?:&back=([^&]+))?/);
  if (!match) return null;
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  try {
    if (match[2]) setSwitchReturn(portalName, decodeURIComponent(match[2]));
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

function backToBackOffice(portalName) {
  window.location.assign(getSwitchReturn(portalName));
}

function isRetryable(err) {
  return !err.response || err.response.status === 502 || err.response.status === 503;
}

// Runs request(), retrying through a cold start. Resolves to the response,
// or rejects with the last error.
async function withRetry(request, onWaking) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await request();
    } catch (err) {
      if (!isRetryable(err) || attempt >= MAX_ATTEMPTS - 1) throw err;
      onWaking(true);
      await sleep(RETRY_DELAY_MS);
    }
  }
}

// Emails a reset link to a login on this portal. The answer is the same
// whether or not the email has a login here.
function PortalForgotPasswordForm({ portalName, branding, initialEmail, onBack }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [saving, setSaving] = useState(false);
  const [sentMessage, setSentMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await portalApi.post(`/api/portal-auth/portals/${encodeURIComponent(portalName)}/forgot-password`, { email });
      setSentMessage(res.data.message);
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not send a reset link'));
    }
    setSaving(false);
  };

  const title = portalTitle(branding.company_name);
  const sendEnvironment = branding.send_environment;
  if (sentMessage) {
    return (
      <AuthCard title={title} branding={branding} subtitle="Check your email">
        <div style={{ fontSize: 14, marginBottom: 20 }}>{sentMessage}</div>
        <button type="button" onClick={onBack} style={LINK_BUTTON}>Back to sign in</button>
      </AuthCard>
    );
  }
  return (
    <AuthCard title={title} branding={branding} subtitle="We'll email you a link to choose a new password">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <label style={LABEL_STYLE}>Email</label>
          <input autoFocus type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} style={INPUT_STYLE} />
        </div>
        {sendEnvironment?.nonProduction && (
          sendEnvironment.testEmailAddress ? (
            <div style={{ fontSize: 12, color: '#8a5a00', background: '#fff8e6', border: '1px solid #f0d9a6', borderRadius: 4, padding: '8px 10px' }}>
              Non-production: the reset link will be delivered to <strong>{sendEnvironment.testEmailAddress}</strong>, overriding the entered email.
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 10px' }}>
              Non-production: sending is blocked until a Test Email Address is set on Platform Settings.
            </div>
          )
        )}
        {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
        <button type="submit" disabled={saving || !email.trim()} style={{ ...PRIMARY_BUTTON, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Sending...' : 'Send reset link'}
        </button>
        <button type="button" onClick={onBack} style={{ ...LINK_BUTTON, alignSelf: 'flex-start' }}>Back to sign in</button>
      </form>
    </AuthCard>
  );
}

// "Need access?" under the sign-in form, with the company's own email and
// phone from Company Settings when set.
function ContactLine({ branding }) {
  const email = branding.contact_email;
  const phone = branding.contact_phone;
  const who = branding.company_name || 'us';
  if (!email && !phone) return <>Need access? Contact {who}.</>;
  return (
    <>
      Need access? Contact {who}
      {email && <> at <a href={`mailto:${email}`} style={{ color: '#1976d2' }}>{email}</a></>}
      {phone && <>{email ? ' or ' : ' on '}<a href={`tel:${phone.replace(/\s+/g, '')}`} style={{ color: '#1976d2' }}>{phone}</a></>}
      .
    </>
  );
}

function PortalSignInScreen({ portalName, branding, notice, onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await portalApi.post(`/api/portal-auth/portals/${encodeURIComponent(portalName)}/sign-in`, { email, password });
      await onSignedIn(res.data.data.token);
      return;
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not sign in'));
    }
    setSaving(false);
  };

  if (showForgot) {
    return <PortalForgotPasswordForm portalName={portalName} branding={branding} initialEmail={email} onBack={() => setShowForgot(false)} />;
  }

  return (
    <AuthCard title={portalTitle(branding.company_name)} branding={branding} subtitle="Sign in to download your pricelists, track your orders and manage your account." footer={<ContactLine branding={branding} />}>
      {notice && <div style={{ background: '#fff8e1', border: '1px solid #f0d58c', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 14 }}>{notice}</div>}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <label style={LABEL_STYLE}>Email</label>
          <input autoFocus type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} style={INPUT_STYLE} />
        </div>
        <div>
          <label style={LABEL_STYLE}>Password</label>
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} style={INPUT_STYLE} />
        </div>
        {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
        <button type="submit" disabled={saving || !email.trim() || !password} style={{ ...PRIMARY_BUTTON, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Signing in...' : 'Sign in'}
        </button>
        <button type="button" onClick={() => setShowForgot(true)} style={{ ...LINK_BUTTON, alignSelf: 'flex-start' }}>Forgot password?</button>
      </form>
    </AuthCard>
  );
}

const CARD_STYLE = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 };

// An error response to a blob request arrives as a Blob; reads its message.
async function blobErrorMessage(err, fallback) {
  if (err.response?.data instanceof Blob) {
    try {
      return JSON.parse(await err.response.data.text()).message || fallback;
    } catch {
      return fallback;
    }
  }
  return portalErrorMessage(err, fallback);
}

const FORMATS = [{ key: 'compact', label: 'Compact' }, { key: 'extended', label: 'Extended' }];
const formatLabel = (format) => (format === 'extended' ? 'Extended' : 'Compact');
const pdfFilename = (name, format) => `${name.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'Pricelist'} - ${formatLabel(format)}.pdf`;

// Preview of one pricelist PDF beside the list (below it on a narrow
// screen), like Compose Email's attachment preview: Compact/Extended tabs,
// Download saves the PDF on screen, Close hides it.
function PricelistViewer({ viewer, onFormat, onClose }) {
  const { pricelist, format, url, loading, error } = viewer;
  const tabStyle = (active) => ({
    fontSize: 12,
    padding: '5px 10px',
    borderRadius: 14,
    fontFamily: 'inherit',
    border: `1px solid ${active ? '#1976d2' : '#d7dbe3'}`,
    background: active ? '#1976d2' : '#fff',
    color: active ? '#fff' : '#333',
    cursor: loading ? 'not-allowed' : 'pointer',
  });
  return (
    <div style={{ flex: '2 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', height: '70vh', minHeight: 420, border: '1px solid #d7dbe3', borderRadius: 8, overflow: 'hidden', background: '#eef1f5' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', background: '#fff', borderBottom: '1px solid #d7dbe3' }}>
        <div style={{ fontSize: 14, fontWeight: 600, flex: '1 1 160px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={pricelist.name}>{pricelist.name}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {FORMATS.map((f) => (
            <button key={f.key} type="button" onClick={() => onFormat(f.key)} disabled={loading || format === f.key} title={`Show the ${f.label} PDF`} style={tabStyle(format === f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => saveBlob(viewer.blob, pdfFilename(pricelist.name, format))}
          disabled={!url}
          title="Save this PDF to your device"
          style={{ ...PRIMARY_BUTTON, cursor: url ? 'pointer' : 'not-allowed', opacity: url ? 1 : 0.6 }}
        >
          Download
        </button>
        <button type="button" onClick={onClose} title="Close the preview" style={SECONDARY_BUTTON}>Close</button>
      </div>
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 14 }}>Preparing your pricelist...</div>
      ) : url ? (
        <iframe title={`${pricelist.name} (${formatLabel(format)})`} src={url} style={{ flex: 1, minHeight: 0, width: '100%', border: 'none' }} />
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'red', fontSize: 14, padding: 24, textAlign: 'center' }}>{error || 'No preview available.'}</div>
      )}
    </div>
  );
}

// The Published pricelists this Customer can download today (issue #255),
// each opened on screen as a Compact or Extended PDF.
function PortalPricelists({ portalName, onSessionEnded }) {
  const [pricelists, setPricelists] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [viewer, setViewer] = useState(null); // { pricelist, format, loading, url, blob, error }
  const requestRef = useRef(0);
  const base = `/api/portal-auth/portals/${encodeURIComponent(portalName)}/pricelists`;

  useEffect(() => {
    let cancelled = false;
    portalApi.get(base, { headers: sessionHeaders(portalName) })
      .then((res) => { if (!cancelled) setPricelists(res.data.data); })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) onSessionEnded();
        else setLoadError(portalErrorMessage(err, 'Could not load your pricelists'));
      });
    return () => { cancelled = true; };
  }, [base, portalName, onSessionEnded]);

  // The object URL on screen is let go whenever the PDF changes or the
  // preview closes.
  const viewerUrl = viewer?.url;
  useEffect(() => () => { if (viewerUrl) window.URL.revokeObjectURL(viewerUrl); }, [viewerUrl]);

  // Each PDF fetched counts as a download (portalPricelistService.js);
  // Download in the preview saves the one already on screen.
  const open = async (pricelist, format) => {
    const request = ++requestRef.current;
    setViewer({ pricelist, format, loading: true, url: '', blob: null, error: '' });
    try {
      const res = await portalApi.get(`${base}/${pricelist.id}/pdf`, { params: { format }, headers: sessionHeaders(portalName), responseType: 'blob' });
      if (request !== requestRef.current) return;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      setViewer({ pricelist, format, loading: false, url: window.URL.createObjectURL(blob), blob, error: '' });
    } catch (err) {
      if (request !== requestRef.current) return;
      if (err.response?.status === 401) {
        setViewer(null);
        onSessionEnded();
        return;
      }
      setViewer({ pricelist, format, loading: false, url: '', blob: null, error: await blobErrorMessage(err, 'Could not open the pricelist') });
    }
  };

  const close = useCallback(() => {
    requestRef.current += 1;
    setViewer(null);
  }, []);

  const validity = (p) => (p.valid_to ? `Valid ${p.valid_from} to ${p.valid_to}` : `Valid from ${p.valid_from}`);

  return (
    <div style={CARD_STYLE}>
      <h1 style={{ margin: '0 0 14px', fontSize: 20, fontWeight: 600 }}>Pricelists</h1>
      {loadError && <div style={{ color: 'red', fontSize: 13 }}>{loadError}</div>}
      {!loadError && pricelists === null && <div style={{ fontSize: 14, color: '#5a6b85' }}>Loading...</div>}
      {pricelists && pricelists.length === 0 && (
        <div style={{ fontSize: 14, color: '#5a6b85' }}>No pricelists are available to you at the moment.</div>
      )}
      {pricelists && pricelists.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: viewer ? '1 1 280px' : '1 1 100%', minWidth: 0 }}>
            {pricelists.map((p, i) => {
              const selected = viewer?.pricelist.id === p.id;
              return (
                <div
                  key={p.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                    padding: '12px 10px', margin: '0 -10px', borderRadius: 6,
                    borderTop: i === 0 ? 'none' : '1px solid #eef1f5',
                    background: selected ? '#eef5fd' : 'transparent',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: '#5a6b85' }}>{validity(p)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {FORMATS.map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => open(p, f.key)}
                        title={`Open the ${f.label} PDF of ${p.name}`}
                        style={SECONDARY_BUTTON}
                      >
                        {f.label} PDF
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {viewer && <PricelistViewer viewer={viewer} onFormat={(format) => open(viewer.pricelist, format)} onClose={close} />}
        </div>
      )}
    </div>
  );
}

// A phone-width screen, where the portal's menu folds behind a Menu button.
const NARROW_QUERY = '(max-width: 720px)';
function useNarrow() {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(NARROW_QUERY);
    const changed = () => setNarrow(query.matches);
    query.addEventListener('change', changed);
    return () => query.removeEventListener('change', changed);
  }, []);
  return narrow;
}

const navButton = (active) => ({ ...SECONDARY_BUTTON, border: 'none', textAlign: 'left', background: active ? '#eef5fd' : 'transparent', color: active ? '#1976d2' : '#1f2d3d', fontWeight: active ? 600 : 400 });

const SWITCH_BANNER_HEIGHT = 44;

// What the Admin can do while switched, spelled out in the banner.
function switchModeText(me) {
  if (me.switch.read_only) return 'Production: nothing can be saved, changed or sent.';
  if (!me.email) return 'Changes are saved as this Customer. With no login, there\'s no password or login email to change.';
  return 'Changes are saved as this login, and emails go to the Test Email Address.';
}

// Fixed across the top of every page while an Admin is switched in.
function SwitchBanner({ me, onExit }) {
  const who = me.email || 'no login';
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, minHeight: SWITCH_BANNER_HEIGHT, boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap',
      padding: '6px 16px', background: '#8a5a00', color: '#fff', fontSize: 13,
    }}>
      <span style={{ wordBreak: 'break-word' }}>
        Viewing as <strong>{me.customer_name}</strong> ({who})
      </span>
      <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
        {me.switch.read_only ? 'Read-only' : 'Self-serve (dev)'}
      </span>
      <span style={{ fontSize: 12 }}>{switchModeText(me)}</span>
      <button type="button" onClick={onExit} title="End the login switch and close the customer's view" style={{ ...SECONDARY_BUTTON, padding: '3px 12px' }}>Exit</button>
    </div>
  );
}

function PortalWelcome({ branding, me, portalName, page, onNavigate, onSignOut, onExitSwitch, onSessionEnded }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const [customerLogoFailed, setCustomerLogoFailed] = useState(false);
  const ordering = me.ordering || { can_order: false, message: '' };
  // Lines in this login's cart, for the Cart button. Read once on arrival,
  // then reported by whatever page changes the cart.
  const [cartCount, setCartCount] = useState(0);
  const refreshCartCount = useCallback(async () => {
    if (!ordering.can_order) return;
    try {
      const res = await portalApi.get(`/api/portal-auth/portals/${encodeURIComponent(portalName)}/cart`, { headers: sessionHeaders(portalName) });
      setCartCount(res.data.data.cart ? res.data.data.cart.lines.length : 0);
    } catch {
      // The count just stays as it was.
    }
  }, [ordering.can_order, portalName]);
  useEffect(() => { refreshCartCount(); }, [refreshCartCount]);
  // Shipments belong to orders, so Orders stays lit on the Shipments page.
  const ordersPage = page === ORDERS_PAGE || page === SHIPMENTS_PAGE || page.startsWith(`${ORDERS_PAGE}/`);
  // A new order starts from the Orders page, so Orders stays lit there too.
  const ordersLit = ordersPage || page === ORDER_PAGE;
  const quotesPage = page === QUOTES_PAGE || page.startsWith(`${QUOTES_PAGE}/`);
  const narrow = useNarrow();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [page, narrow]);
  // Roomier to tap on a phone.
  const menuItem = (active) => (narrow ? { ...navButton(active), padding: '10px 12px' } : navButton(active));
  const cartButton = ordering.can_order && (
    <button type="button" onClick={() => onNavigate(CART_PAGE)} title="Show your cart" style={menuItem(page === CART_PAGE)}>Cart{cartCount ? ` (${cartCount})` : ''}</button>
  );
  const menuButtons = (
    <>
      <button type="button" onClick={() => onNavigate('')} title="Show the welcome page" style={menuItem(page === '')}>Home</button>
      <button type="button" onClick={() => onNavigate(PRICELISTS_PAGE)} title="Show your pricelists" style={menuItem(page === PRICELISTS_PAGE)}>Pricelists</button>
      {!narrow && cartButton}
      <button type="button" onClick={() => onNavigate(QUOTES_PAGE)} title="Show your quotes" style={menuItem(quotesPage)}>Quotes</button>
      <button type="button" onClick={() => onNavigate(ORDERS_PAGE)} title="Show your orders" style={menuItem(ordersLit)}>Orders</button>
      <button type="button" onClick={() => onNavigate(TERMS_PAGE)} title="Show our Terms & Conditions of Trade" style={menuItem(page === TERMS_PAGE)}>Terms</button>
      <button type="button" onClick={() => onNavigate(ACCOUNT_PAGE)} title={me.email ? 'Show your Preferences, password and login email' : 'Show your Preferences'} style={menuItem(page === ACCOUNT_PAGE)}>My account</button>
    </>
  );
  const signedIn = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, ...(narrow ? {} : { marginLeft: 8, paddingLeft: 14, borderLeft: '1px solid #e3e7ee' }) }}>
      {me.customer_logo_id && !customerLogoFailed && (
        <img
          src={previewFileUrl(me.customer_logo_id)}
          alt={me.customer_name}
          onError={() => setCustomerLogoFailed(true)}
          style={{ maxHeight: 36, maxWidth: 120, objectFit: 'contain' }}
        />
      )}
      <div style={{ lineHeight: 1.3, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#5a6b85' }}>Signed in as</div>
        <div style={{ fontWeight: 600, color: '#1f2d3d' }}>{me.customer_name}</div>
        {me.email && <div style={{ fontSize: 12, color: '#5a6b85', wordBreak: 'break-all' }}>{me.email}</div>}
      </div>
    </div>
  );
  const signOutButton = !me.switch && <button type="button" onClick={onSignOut} style={SECONDARY_BUTTON}>Sign out</button>;
  return (
    <div style={{ minHeight: '100vh', background: AUTH_BACKGROUND, fontFamily: FONT, color: '#1f2d3d', paddingTop: me.switch ? SWITCH_BANNER_HEIGHT : 0 }}>
      {me.switch && <SwitchBanner me={me} onExit={onExitSwitch} />}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: narrow ? '10px 16px' : '12px 20px', background: '#fff', borderBottom: '1px solid #e3e7ee' }}>
        <div style={{ display: 'flex', alignItems: 'center', minHeight: 40, minWidth: 0 }}>
          {branding.logo_id && !logoFailed ? (
            <img
              src={mediaFileUrl(branding.logo_id)}
              alt={branding.company_name || 'Company logo'}
              onError={() => setLogoFailed(true)}
              style={{ maxHeight: 40, maxWidth: narrow ? 140 : 200, objectFit: 'contain' }}
            />
          ) : (
            <span style={{ fontWeight: 600, fontSize: 16 }}>{branding.company_name || 'Customer'}</span>
          )}
          {!narrow && (
            <span style={{ marginLeft: 12, paddingLeft: 12, borderLeft: '1px solid #d7dbe3', fontSize: 15, fontWeight: 600, color: '#5a6b85', whiteSpace: 'nowrap' }}>
              Customer Portal
            </span>
          )}
        </div>
        {narrow ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              {cartButton}
              <button type="button" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen} title={menuOpen ? 'Close the menu' : 'Open the menu'} style={{ ...SECONDARY_BUTTON, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>{menuOpen ? '✕' : '☰'}</span> Menu
              </button>
            </div>
            {menuOpen && (
              <nav style={{ flexBasis: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2, paddingTop: 8, borderTop: '1px solid #e3e7ee', fontSize: 14 }}>
                {menuButtons}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8, paddingTop: 12, borderTop: '1px solid #e3e7ee' }}>
                  {signedIn}
                  {signOutButton}
                </div>
              </nav>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
            {menuButtons}
            {signedIn}
            {signOutButton}
          </div>
        )}
      </header>
      <main style={{ maxWidth: 1100, margin: narrow ? '16px auto' : '32px auto', padding: '0 16px' }}>
        {page === ACCOUNT_PAGE ? (
          <PortalAccountPage portalName={portalName} onSessionEnded={onSessionEnded} />
        ) : page === TERMS_PAGE ? (
          <PortalTermsPage portalName={portalName} onSessionEnded={onSessionEnded} />
        ) : page === SHIPMENTS_PAGE ? (
          <PortalShipmentsPage portalName={portalName} onNavigate={onNavigate} onSessionEnded={onSessionEnded} />
        ) : page === ORDER_PAGE ? (
          <PortalOrderPage portalName={portalName} ordering={ordering} cartCount={cartCount} onCartChanged={refreshCartCount} onNavigate={onNavigate} onSessionEnded={onSessionEnded} />
        ) : page === CART_PAGE ? (
          <PortalCartPage portalName={portalName} ordering={ordering} onCartChanged={setCartCount} onNavigate={onNavigate} onSessionEnded={onSessionEnded} />
        ) : quotesPage ? (
          <PortalQuotesPage portalName={portalName} quoteId={page.split('/')[1] || ''} onNavigate={onNavigate} onSessionEnded={onSessionEnded} />
        ) : ordersPage ? (
          <PortalOrdersPage portalName={portalName} orderId={page.split('/')[1] || ''} ordering={ordering} onNavigate={onNavigate} onCartChanged={refreshCartCount} onSessionEnded={onSessionEnded} />
        ) : page === PRICELISTS_PAGE ? (
          <PortalPricelists portalName={portalName} onSessionEnded={onSessionEnded} />
        ) : (
          <PortalHome portalName={portalName} me={me} branding={branding} onNavigate={onNavigate} onSessionEnded={onSessionEnded} />
        )}
      </main>
    </div>
  );
}

function PortalGate({ portalName }) {
  const [status, setStatus] = useState('checking'); // checking | not_found | unreachable | sign_in | switch_ended | ok
  const [branding, setBranding] = useState(null);
  const [me, setMe] = useState(null);
  const [notice, setNotice] = useState('');
  const [waking, setWaking] = useState(false);
  const [page, setPage] = useState(portalPageFromPath);
  const statusRef = useRef(status);
  statusRef.current = status;
  const base = `/api/portal-auth/portals/${encodeURIComponent(portalName)}`;

  // A switch tab never touches the real portal session: it only drops its
  // own switch token.
  const sessionEnded = useCallback((message) => {
    setMe(null);
    if (getPortalSwitch(portalName)) {
      setPortalSwitch(portalName, null);
      setStatus('switch_ended');
      return;
    }
    setPortalSession(portalName, null);
    setNotice(message);
    setStatus('sign_in');
  }, [portalName]);

  // The signed-in login (or switched-into one), or null when there's no
  // live session.
  const loadMe = useCallback(async () => {
    if (!getPortalSession(portalName) && !getPortalSwitch(portalName)) return null;
    try {
      const res = await withRetry(() => portalApi.get(`${base}/me`, { headers: sessionHeaders(portalName) }), setWaking);
      return res.data.data;
    } catch (err) {
      if (err.response?.status === 401) return null;
      throw err;
    }
  }, [base, portalName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await withRetry(() => portalApi.get(base), setWaking);
        if (cancelled) return;
        setBranding(res.data.data);
        const handoff = takeSwitchHandoff(portalName);
        if (handoff) {
          try {
            const claimed = await portalApi.post(`${base}/switch/claim`, { token: handoff });
            setPortalSwitch(portalName, claimed.data.data.token);
          } catch (err) {
            if (err.response?.status !== 401) throw err;
            if (cancelled) return;
            setWaking(false);
            setStatus('switch_ended');
            return;
          }
        }
        const switching = Boolean(getPortalSwitch(portalName));
        const signedIn = await loadMe();
        if (cancelled) return;
        setWaking(false);
        if (signedIn) {
          setMe(signedIn);
          setStatus('ok');
        } else if (switching) {
          setPortalSwitch(portalName, null);
          setStatus('switch_ended');
        } else {
          setPortalSession(portalName, null);
          setStatus('sign_in');
        }
      } catch (err) {
        if (cancelled) return;
        setWaking(false);
        setStatus(err.response?.status === 404 ? 'not_found' : 'unreachable');
      }
    })();
    return () => { cancelled = true; };
  }, [base, portalName, loadMe]);

  // A Customer switched off or paused while signed in is signed out on
  // their next action — coming back to the tab counts as one.
  useEffect(() => {
    const recheck = async () => {
      if (statusRef.current !== 'ok' || document.visibilityState === 'hidden') return;
      try {
        const signedIn = await loadMe();
        if (!signedIn && statusRef.current === 'ok') sessionEnded(SESSION_ENDED_NOTICE);
      } catch {
        // Couldn't reach the server — stay put; the next action checks again.
      }
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [loadMe, sessionEnded]);

  // A switch can end while the tab sits untouched (time up, or the Admin
  // started another or signed out of the back office), so the tab checks
  // without counting as activity.
  const switchActive = status === 'ok' && Boolean(me?.switch);
  useEffect(() => {
    if (!switchActive) return undefined;
    const timer = setInterval(async () => {
      try {
        await portalApi.get('/api/portal-auth/switch/status', { headers: sessionHeaders(portalName) });
      } catch (err) {
        if (err.response?.status === 401) sessionEnded('');
      }
    }, SWITCH_STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [switchActive, portalName, sessionEnded]);

  const exitSwitch = useCallback(async () => {
    try {
      await portalApi.post('/api/portal-auth/switch/exit', null, { headers: sessionHeaders(portalName) });
    } catch {
      // Ended on this tab either way; the switch runs out on its own.
    }
    setPortalSwitch(portalName, null);
    backToBackOffice(portalName);
  }, [portalName]);

  const handleSignedIn = useCallback(async (token) => {
    setPortalSession(portalName, token);
    const signedIn = await loadMe();
    if (!signedIn) throw new Error('Could not sign in');
    setNotice('');
    setMe(signedIn);
    setStatus('ok');
  }, [portalName, loadMe]);

  const endedWhileSignedIn = useCallback(() => sessionEnded(SESSION_ENDED_NOTICE), [sessionEnded]);

  // Each page has its own address, so Back and a bookmark work.
  const navigate = useCallback((next) => {
    const path = `/portal/${encodeURIComponent(portalName)}${next ? `/${next}` : ''}`;
    if (window.location.pathname !== path) window.history.pushState(null, '', path);
    setPage(next);
    window.scrollTo(0, 0);
  }, [portalName]);

  useEffect(() => {
    const onPop = () => setPage(portalPageFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await portalApi.post('/api/portal-auth/sign-out', null, { headers: sessionHeaders(portalName) });
    } catch {
      // Signed out locally either way.
    }
    sessionEnded('');
  }, [portalName, sessionEnded]);

  if (status === 'checking') {
    return <AuthMessage>{waking ? 'Connecting to the server. It may be starting up, which can take up to a minute...' : ''}</AuthMessage>;
  }
  if (status === 'not_found') {
    return (
      <AuthCard title="Customer Portal" branding={null} subtitle="Portal not found">
        <div style={{ fontSize: 14 }}>There's no customer portal at this address. Check the address you were given.</div>
      </AuthCard>
    );
  }
  if (status === 'unreachable') {
    return (
      <AuthCard title="Customer Portal" branding={null} subtitle="Can't connect">
        <div style={{ fontSize: 14 }}>Could not reach the server. Try again in a moment.</div>
      </AuthCard>
    );
  }
  if (status === 'switch_ended') {
    return (
      <AuthCard title={portalTitle(branding?.company_name)} branding={branding} subtitle="Login switch ended">
        <div style={{ fontSize: 14 }}>To view as a customer again, start a new switch from the back office.</div>
        <div style={{ marginTop: 16 }}>
          <button type="button" onClick={() => backToBackOffice(portalName)} title="Go to the back office" style={{ ...PRIMARY_BUTTON, cursor: 'pointer' }}>
            Back to the back office
          </button>
        </div>
      </AuthCard>
    );
  }
  if (status === 'sign_in') {
    return <PortalSignInScreen portalName={portalName} branding={branding} notice={notice} onSignedIn={handleSignedIn} />;
  }
  return <PortalWelcome branding={branding} me={me} portalName={portalName} page={page} onNavigate={navigate} onSignOut={signOut} onExitSwitch={exitSwitch} onSessionEnded={endedWhileSignedIn} />;
}

// /portal with no portal name, where the old WooCommerce site's My Account
// addresses now redirect (issue #366): goes on to this website's own
// Customer portal, the one its Customer portal buttons open.
function PortalDoor() {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    portalApi.get('/api/site/profile')
      .then((res) => {
        const portalPath = res.data?.data?.portal_path;
        if (portalPath) window.location.replace(portalPath);
        else setFailed(true);
      })
      .catch(() => setFailed(true));
  }, []);
  if (!failed) return <AuthMessage>Opening the customer portal...</AuthMessage>;
  return (
    <AuthCard title="Customer Portal" branding={null} subtitle="Portal not found">
      <div style={{ fontSize: 14 }}>There's no customer portal at this address. Check the address you were given.</div>
    </AuthCard>
  );
}

function PortalApp() {
  const path = window.location.pathname;
  if (path === PORTAL_SET_PASSWORD_PATH || path === PORTAL_RESET_PASSWORD_PATH) return <PortalSetPasswordScreen />;
  if (path === PORTAL_CONFIRM_EMAIL_PATH) return <PortalConfirmEmailScreen />;
  const portalName = portalNameFromPath();
  if (!portalName) return <PortalDoor />;
  return <PortalGate key={portalName} portalName={portalName} />;
}

export default PortalApp;
